<!-- eslint-disable vue/no-v-html -->
<template>
    <div class="ask-pendo-reasoning">
        <div
            class="ask-pendo-reasoning__icon"
            :class="{
                'ask-pendo-reasoning__icon--animated': showAnimatedHeart,
                'ask-pendo-reasoning__icon--static': !showAnimatedHeart,
            }">
            <img
                :src="iconSrc"
                :alt="iconAlt"
                :class="iconClass">
        </div>
        <div class="ask-pendo-reasoning__stack">
            <pendo-collapse-transition>
                <div v-show="!pending">
                    <div
                        class="toggle"
                        @click="toggleCollapsed">
                        <span>{{ $t('askPendo.Reasoning') }}</span>
                        <pendo-icon
                            :type="collapsed ? 'chevron-right' : 'chevron-down'"
                            :stroke-width="2"
                            :size="16"
                            :stroke="iconStroke" />
                    </div>
                </div>
            </pendo-collapse-transition>
            <pendo-collapse-transition>
                <div
                    v-show="pending || !collapsed"
                    :key="rowsKey"
                    :class="{ 'reasoning-timeline': !collapsed }">
                    <div
                        v-for="row in displayedRows"
                        :key="row.id"
                        :class="[
                            'ask-pendo-reasoning__row',
                            { 'reasoning-border': row.complete, 'complete': row.complete },
                        ]"
                        :data-complete="row.complete">
                        <div :class="['message', 'message--tool', { timeline: row.complete }]">
                            <span
                                v-if="row.complete"
                                class="tool-chip">
                                <pendo-icon
                                    type="check"
                                    :stroke-width="2"
                                    :size="14"
                                    class="tool-chip__icon" />
                            </span>
                            <div
                                v-if="row.html"
                                :class="['message', 'message--reasoning', { 'message--animating': !row.complete }]"
                                v-html="row.html" />
                            <span
                                v-else
                                :class="{ 'message__text--bold': row.complete }">{{ row.text }}</span>
                        </div>
                    </div>
                </div>
            </pendo-collapse-transition>
        </div>
    </div>
</template>

<script>
import { PendoCollapseTransition, PendoIcon } from '@pendo/components';
import { hasReasoningSummaries, isToolCall, isToolResponse, isSubagentHandoff } from '@/ask-pendo/utils/message';
import { parseMessageContent } from '@/vuex/modules/ask-pendo/ask-pendo.utils';
import { renderMarkdown } from '@/common/marked/markdown.utils';
import { v4 as uuid } from 'uuid';
import { getComputedColor } from '@/utils/color';

export default {
    name: 'AskPendoReasoning',
    components: {
        PendoCollapseTransition,
        PendoIcon,
    },
    props: {
        messages: {
            type: Array,
            required: true,
        },
        pending: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['visible-messages-changed'],
    data () {
        return {
            collapsed: true,
            // MCP tools that contain subgraphs can stream back tool calls that never have an associated response. This
            // is a list of those possible tool names, which we do not want to show in the UI since it will appear that
            // a tool call is never resolved (which is not the case).
            ignoredTools: ['TagSearchResults'],
            iconStroke: getComputedColor('--palette-gray-60'),
        };
    },
    computed: {
        iconSrc () {
            return this.showAnimatedHeart ? require('@img/agents/thinking.gif') : require('@img/pendo-chevron.svg');
        },
        iconAlt () {
            return this.showAnimatedHeart
                ? this.$t('askPendo.thinkingIcon.Pendo Logo Thinking Heart Icon')
                : this.$t('askPendo.assistantIcon.Pendo Icon');
        },
        iconClass () {
            return this.showAnimatedHeart
                ? 'ask-pendo-reasoning__icon--animated-img'
                : 'ask-pendo-reasoning__icon--static-img';
        },
        completedToolCallIds () {
            return this.messages.filter(isToolResponse).map((message) => message.tool_call_id);
        },
        toolCallMessages () {
            // Get tool calls from subagent handoff messages
            const subagentHandoffToolCalls = (message) => {
                if (!isSubagentHandoff(message)) {
                    return [];
                }

                return (message.metadata?.subagent_tool_calls || []).map((toolName) => ({
                    type: 'subagentHandoff',
                    toolName,
                }));
            };

            const isVisibleToolCall = (message) =>
                hasReasoningSummaries(message) ||
                message.type === 'reasoning' ||
                message.type === 'progress' ||
                message.type === 'custom_instructions' ||
                (isToolCall(message) && !this.ignoredTools.includes(message.tool_calls[0]?.name));

            return this.messages.flatMap((message) => [
                ...(isVisibleToolCall(message) ? [message] : []),
                ...subagentHandoffToolCalls(message),
            ]);
        },
        genericThinkingString () {
            return this.$t('askPendo.Thinking');
        },
        visibleRows () {
            const rows = [];

            // Only push a row if its text is different from the last row
            const pushIfNew = (row) => {
                const prevRow = rows[rows.length - 1];
                const currentText = row.text || row.html || '';
                const prevText = prevRow?.text || prevRow?.html || '';

                if (!prevRow || currentText !== prevText) {
                    rows.push(row);
                }
            };

            // Map tool calls to display rows
            this.toolCallMessages.forEach((message) => {
                // Handle custom_instructions first - should appear at top of reasoning
                if (message.type === 'custom_instructions') {
                    rows.unshift({
                        id: message.id || 'custom-instructions',
                        isReasoning: false,
                        complete: this.pending ? false : message.status === 'complete',
                        text: this.pending ? `${message.message}...` : message.message,
                    });

                    return;
                }

                if (hasReasoningSummaries(message)) {
                    const contentBlock = parseMessageContent(message);

                    if (contentBlock.reasoningSummaries) {
                        contentBlock.reasoningSummaries.forEach((summary) => {
                            pushIfNew({
                                id: `${message.id}-${summary.index}`,
                                isReasoning: true,
                                complete: true,
                                html: renderMarkdown(summary.text ?? ''),
                            });
                        });
                    }
                }

                if (message.type === 'reasoning') {
                    pushIfNew({
                        id: message.id,
                        isReasoning: true,
                        complete: false,
                        text: message.content ?? '',
                    });
                }

                if (isToolCall(message)) {
                    pushIfNew({
                        id: message.id,
                        isReasoning: false,
                        toolName: message.tool_calls?.[0]?.name ?? 'tool',
                        // While streaming, show all tool calls as incomplete to maintain present tense
                        complete: this.pending ? false : this.isToolCallComplete(message),
                        text: this.getToolReasoningText(message),
                    });
                }

                if (message.type === 'progress') {
                    pushIfNew({
                        id: message.id || uuid(),
                        isReasoning: false,
                        complete: message.complete || false,
                        text: message.content,
                    });
                }

                if (message.type === 'subagentHandoff') {
                    const toolText = this.toolCallDisplayStringsMap[message.toolName];

                    pushIfNew({
                        id: uuid(),
                        isReasoning: false,
                        toolName: message.toolName,
                        complete: true, // Always complete, as it's from the subagent->supervisor handoff
                        text: toolText?.done || this.genericThinkingString,
                    });
                }
            });

            if (this.isThinking) {
                pushIfNew({ id: 'thinking', complete: false, text: `${this.genericThinkingString}...` });
            }

            return rows;
        },
        displayedRows () {
            if (this.collapsed && this.pending) {
                // Single-line view, only show the last item
                const last = this.visibleRows[this.visibleRows.length - 1];

                return last ? [last] : [];
            }

            return this.visibleRows;
        },
        toolCallDisplayStringsMap () {
            // Provide both ongoing (present/progressive) and done (past) variants per tool.
            const t = (key) => this.$t(key);

            return {
                accountMetadataSchema: {
                    ongoing: t('askPendo.thinkingMessages.Retrieving account metadata'),
                    done: t('askPendo.thinkingMessagesDone.Retrieved account metadata'),
                },
                accountQuery: {
                    ongoing: t('askPendo.thinkingMessages.Retrieving account data'),
                    done: t('askPendo.thinkingMessagesDone.Retrieved account data'),
                },
                activityQuery: {
                    ongoing: t('askPendo.thinkingMessages.Analyzing activity data'),
                    done: t('askPendo.thinkingMessagesDone.Analyzed activity data'),
                },
                aggregationQueryBuilder: {
                    ongoing: t('askPendo.thinkingMessages.Setting up your data query'),
                    done: t('askPendo.thinkingMessagesDone.Set up your data query'),
                },
                analyze_user_intent_for_filters: {
                    ongoing: t('askPendo.thinkingMessages.Retrieving visitor metadata'),
                    done: t('askPendo.thinkingMessagesDone.Retrieved visitor metadata'),
                },
                create_highcharts_tool: {
                    ongoing: t('askPendo.thinkingMessages.Creating a visualization'),
                    done: t('askPendo.thinkingMessagesDone.Created a visualization'),
                },
                create_table_artifact: {
                    ongoing: t('askPendo.thinkingMessages.Creating a table'),
                    done: t('askPendo.thinkingMessagesDone.Created a table'),
                },
                create_account_health_document: {
                    ongoing: t('askPendo.thinkingMessages.Creating an Account Health report'),
                    done: t('askPendo.thinkingMessagesDone.Created an Account Health report'),
                },
                decision: {
                    ongoing: t('askPendo.thinkingMessages.Reviewing results'),
                    done: t('askPendo.thinkingMessagesDone.Reviewed results'),
                },
                dx_report: {
                    ongoing: t('askPendo.thinkingMessages.Creating a Data Explorer report'),
                    done: t('askPendo.thinkingMessagesDone.Created a Data Explorer report'),
                },
                find_pages: {
                    ongoing: t('askPendo.thinkingMessages.Searching for Pages'),
                    done: t('askPendo.thinkingMessagesDone.Searched for Pages'),
                },
                find_page_or_feature: {
                    ongoing: t('askPendo.thinkingMessages.Searching for Pages and Features'),
                    done: t('askPendo.thinkingMessagesDone.Searched for Pages and Features'),
                },
                generate_table: {
                    ongoing: t('askPendo.thinkingMessages.Creating table'),
                    done: t('askPendo.thinkingMessagesDone.Created table'),
                },
                get_accounts_by_id: {
                    ongoing: t('askPendo.thinkingMessages.Retrieving account metadata'),
                    done: t('askPendo.thinkingMessagesDone.Retrieved account metadata'),
                },
                get_visitors_by_id: {
                    ongoing: t('askPendo.thinkingMessages.Retrieving visitor metadata'),
                    done: t('askPendo.thinkingMessagesDone.Retrieved visitor metadata'),
                },
                guideMetrics: {
                    ongoing: t('askPendo.thinkingMessages.Calculating Guide metrics'),
                    done: t('askPendo.thinkingMessagesDone.Calculated Guide metrics'),
                },
                list_all_applications: {
                    ongoing: t('askPendo.thinkingMessages.Loading your apps'),
                    done: t('askPendo.thinkingMessagesDone.Loaded your apps'),
                },
                lookup_countable: {
                    ongoing: t('askPendo.thinkingMessages.Finding events'),
                    done: t('askPendo.thinkingMessagesDone.Found events'),
                },
                planning: {
                    ongoing: t('askPendo.thinkingMessages.Creating a plan'),
                    done: t('askPendo.thinkingMessagesDone.Created a plan'),
                },
                productEngagementScore: {
                    ongoing: t('askPendo.thinkingMessages.Calculating product engagement scores'),
                    done: t('askPendo.thinkingMessagesDone.Calculated product engagement scores'),
                },
                runQuery: {
                    ongoing: t('askPendo.thinkingMessages.Analyzing your data'),
                    done: t('askPendo.thinkingMessagesDone.Analyzed your data'),
                },
                run_qualitative_report: {
                    ongoing: t('askPendo.thinkingMessages.Analyzing customer feedback'),
                    done: t('askPendo.thinkingMessagesDone.Analyzed customer feedback'),
                },
                segmentList: {
                    ongoing: t('askPendo.thinkingMessages.Loading available segments'),
                    done: t('askPendo.thinkingMessagesDone.Loaded available segments'),
                },
                search_for_feedback: {
                    ongoing: t('askPendo.thinkingMessages.Searching customer feedback'),
                    done: t('askPendo.thinkingMessagesDone.Searched customer feedback'),
                },
                searchEntities: {
                    ongoing: t('askPendo.thinkingMessages.Searching for items'),
                    done: t('askPendo.thinkingMessagesDone.Searched for items'),
                },
                sessionReplayList: {
                    ongoing: t('askPendo.thinkingMessages.Searching for session replays'),
                    done: t('askPendo.thinkingMessagesDone.Searched for session replays'),
                },

                tag_search_tool: {
                    ongoing: t('askPendo.thinkingMessages.Searching for Pages and Features'),
                    done: t('askPendo.thinkingMessagesDone.Searched for Pages and Features'),
                },
                top_user_table: {
                    ongoing: t('askPendo.thinkingMessages.Generating a list of top visitors'),
                    done: t('askPendo.thinkingMessagesDone.Generated a list of top visitors'),
                },
                topEntities: {
                    ongoing: t('askPendo.thinkingMessages.Finding top results'),
                    done: t('askPendo.thinkingMessagesDone.Found top results'),
                },
                used_page_or_feature: {
                    ongoing: t('askPendo.thinkingMessages.Getting a list of visitors'),
                    done: t('askPendo.thinkingMessagesDone.Got a list of visitors'),
                },
                visited_page: {
                    ongoing: t('askPendo.thinkingMessages.Getting a list of visitors'),
                    done: t('askPendo.thinkingMessagesDone.Got a list of visitors'),
                },
                visitorActivity: {
                    ongoing: t('askPendo.thinkingMessages.Loading visitor timeline'),
                    done: t('askPendo.thinkingMessagesDone.Loaded visitor timeline'),
                },
                visitorMetadataSchema: {
                    ongoing: t('askPendo.thinkingMessages.Retrieving visitor metadata'),
                    done: t('askPendo.thinkingMessagesDone.Retrieved visitor metadata'),
                },
                visitorQuery: {
                    ongoing: t('askPendo.thinkingMessages.Retrieving visitor data'),
                    done: t('askPendo.thinkingMessagesDone.Retrieved visitor data'),
                },

                // Claude SDK built-in tools
                Write: {
                    ongoing: t('askPendo.thinkingMessages.Writing file'),
                    done: t('askPendo.thinkingMessagesDone.Wrote file'),
                },
                Read: {
                    ongoing: t('askPendo.thinkingMessages.Reading file'),
                    done: t('askPendo.thinkingMessagesDone.Read file'),
                },
                Edit: {
                    ongoing: t('askPendo.thinkingMessages.Editing file'),
                    done: t('askPendo.thinkingMessagesDone.Edited file'),
                },
                Bash: {
                    ongoing: t('askPendo.thinkingMessages.Running command'),
                    done: t('askPendo.thinkingMessagesDone.Ran command'),
                },
                Glob: {
                    ongoing: t('askPendo.thinkingMessages.Searching for files'),
                    done: t('askPendo.thinkingMessagesDone.Searched for files'),
                },
                Grep: {
                    ongoing: t('askPendo.thinkingMessages.Searching file contents'),
                    done: t('askPendo.thinkingMessagesDone.Searched file contents'),
                },
                Task: {
                    ongoing: t('askPendo.thinkingMessages.Running subtask'),
                    done: t('askPendo.thinkingMessagesDone.Ran subtask'),
                },
                TodoWrite: {
                    ongoing: t('askPendo.thinkingMessages.Updating plan'),
                    done: t('askPendo.thinkingMessagesDone.Updated plan'),
                },

                // MCP tool aliases (stripped names that differ from existing LangGraph names)
                create_highcharts_chart: {
                    ongoing: t('askPendo.thinkingMessages.Creating a visualization'),
                    done: t('askPendo.thinkingMessagesDone.Created a visualization'),
                },
                create_generic_document: {
                    ongoing: t('askPendo.thinkingMessages.Creating a document'),
                    done: t('askPendo.thinkingMessagesDone.Created a document'),
                },
                search_entities: {
                    ongoing: t('askPendo.thinkingMessages.Searching for items'),
                    done: t('askPendo.thinkingMessagesDone.Searched for items'),
                },
                search_filters: {
                    ongoing: t('askPendo.thinkingMessages.Searching filters'),
                    done: t('askPendo.thinkingMessagesDone.Searched filters'),
                },
                get_survey_scores: {
                    ongoing: t('askPendo.thinkingMessages.Retrieving survey scores'),
                    done: t('askPendo.thinkingMessagesDone.Retrieved survey scores'),
                },
            };
        },
        hasReasoning () {
            return this.messages.some((msg) => msg?.type === 'reasoning');
        },
        isThinking () {
            // Show generic "Thinking..." only if nothing (no tools and no reasoning) has appeared yet
            return this.pending && this.toolCallMessages.length === 0 && !this.hasReasoning;
        },
        showAnimatedHeart () {
            return this.pending;
        },
        rowsKey () {
            const mode = this.collapsed && this.pending ? 'single' : 'list';
            const last = this.visibleRows[this.visibleRows.length - 1]?.id || 'none';
            const len = this.visibleRows.length;
            const think = this.isThinking ? 'thinking' : 'not-thinking';

            return `${mode}-${last}-${len}-${think}`;
        },
    },
    watch: {
        toolCallMessages (curr, prev) {
            if (curr.length !== prev.length) {
                this.$emit('visible-messages-changed');
            }
        },
        isThinking (curr, prev) {
            if (curr !== prev) {
                this.$emit('visible-messages-changed');
            }
        },
    },
    methods: {
        getToolReasoningText (message) {
            const isDone = this.isToolCallComplete(message);
            const rawName = message.tool_calls?.[0]?.name ?? 'tool';
            // Strip MCP prefix (e.g. "mcp__pendo__activityQuery" -> "activityQuery")
            const name = rawName.startsWith('mcp__') ? rawName.split('__').pop() : rawName;
            const entry = this.toolCallDisplayStringsMap[name];
            // While streaming (pending), always show ongoing/present-tense copy with ellipsis for all rows.
            // This avoids showing past tense until the entire response is finished.
            if (this.pending) {
                const ongoing = (entry && typeof entry === 'object' && entry.ongoing) || this.genericThinkingString;

                return `${ongoing}...`;
            }

            // When not pending (streaming complete), show past tense for completed tools, present tense for incomplete
            if (entry && typeof entry === 'object') {
                const text = isDone ? entry.done : entry.ongoing;

                return isDone ? text : `${text}...`;
            }

            // Fallback copy
            return isDone ? this.genericThinkingString : `${this.genericThinkingString}...`;
        },
        isToolCallComplete (message) {
            if (!this.pending) return true;

            return this.completedToolCallIds.includes(message.tool_calls?.[0]?.id);
        },
        toggleCollapsed () {
            this.collapsed = !this.collapsed;
        },
    },
};
</script>

<style scoped lang="less">
.ask-pendo-reasoning {
    display: flex;
    position: relative;
    gap: 12px;
    white-space: normal;
    margin-bottom: 14px;

    &__icon {
        position: absolute;
        left: -28px;

        display: flex;
        align-items: center;
        justify-content: center;
        height: 28px;

        &--animated {
            left: -32px;
            width: 28px;

            img {
                height: 32px;
                width: 51px;
                margin-right: 4px;
            }
        }

        &--static {
            left: -28px;
            width: 16px;

            img {
                height: 16px;
                width: 16px;
            }
        }
    }

    &__stack {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__row {
        display: flex;
        align-items: center;
        gap: 8px;
        line-height: 28px;
        font-size: 16px;

        .message {
            mask-image: linear-gradient(90deg, #000, #000 30%, #00000065 50%, #000 70%, #000 100%);
            mask-size: 400% 100%;
            animation: think 3s infinite reverse;
        }

        .message--reasoning {
            line-height: 22px;

            :deep(b),
            :deep(strong) {
                font-weight: 600;
            }
        }

        .message__text--bold {
            font-weight: 600;
        }

        .message--tool {
            display: inline-flex;
            align-items: start;
            gap: 8px;
            line-height: 22px;
        }
        .tool-chip {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: var(--palette-gray-10);
            border: 1px solid var(--palette-gray-20);
            border-radius: 999px;
            padding: 2px 8px;
            height: 22px;
        }
        .tool-chip__icon {
            color: var(--palette-gray-70);
        }

        &.complete {
            color: var(--palette-gray-70);
            font-size: 14px;

            .message {
                mask-image: none;
                animation: none;
            }
        }
    }
}

.message--animating {
    mask-image: linear-gradient(90deg, #000, #000 30%, #00000065 50%, #000 70%, #000 100%);
    mask-size: 400% 100%;
    animation: think 3s infinite reverse;
}

.timeline {
    margin-left: -25px;
}

.reasoning-border {
    border-left: 3px solid var(--palette-gray-20);
    padding-left: 8px;
    margin-bottom: 12px;
}

.toggle {
    display: flex;
    align-items: center;
    gap: 4px;

    padding: 0 4px;
    margin-left: -4px;
    border-radius: 3px;

    color: var(--palette-gray-70);
    cursor: pointer;

    font-size: 16px;

    &:hover {
        background-color: var(--palette-gray-10);
    }
}

.reasoning-timeline {
    margin-top: 8px;
}

@keyframes think {
    100% {
        mask-position: right;
    }
}
</style>
